import { serializeJsonFormat } from "@redotech/json/serialize";
import { useMemo, useRef, useState } from "react";
import { useHandler } from "./hook";
import { useLoad } from "./load";

export type Readonly<T> =
  T extends Array<infer E>
    ? readonly E[]
    : T extends string
      ? T
      : T extends Date
        ? Date
        : T extends Temporal.Instant
          ? Temporal.Instant
          : T extends Temporal.PlainDate
            ? Temporal.PlainDate
            : { readonly [P in keyof T]: Readonly<T[P]> };

export function useRpcData<
  C extends Record<string, (...args: any[]) => any>,
  K extends keyof C & string,
  Mutations extends (keyof C & string)[],
>(
  client: C,
  rpc: K,
  params: {
    input: Parameters<C[K]>[0];
    mutations?: Mutations;
    condition?: boolean;
    cache?: boolean;
    cacheKey?: (input: Parameters<C[K]>[0]) => string;
  },
): {
  value?: Readonly<Awaited<ReturnType<C[K]>>>;
  pending: boolean;
  error?: unknown;
  refresh: (clearAll?: boolean) => void;
  mutations: { [key in Mutations[number]]: C[key] };
} {
  type Output = Awaited<ReturnType<C[K]>>;

  const cache = useRef<Map<string, Readonly<Output>>>(new Map());
  const cacheKey = useMemo(
    () =>
      params.cacheKey
        ? params.cacheKey(params.input)
        : JSON.stringify(serializeJsonFormat.write(params.input)),
    [params],
  );
  const [reloadSymbol, setReloadSymbol] = useState(Symbol());

  const dataLoad = useLoad(
    async (signal) => {
      if (params.condition === false) {
        return;
      }
      if (params.cache && cache.current.has(cacheKey)) {
        return cache.current.get(cacheKey)!;
      }
      const response = (await client[rpc]!(params.input, { signal })) as Output;
      if (params.cache && typeof response !== "function") {
        cache.current.set(cacheKey, response as Readonly<Output>);
      }
      return response;
    },
    [cacheKey, reloadSymbol, params.condition],
  );

  const cachedValue = useMemo(() => {
    return params.cache ? cache.current.get(cacheKey) : undefined;
  }, [cacheKey, params.cache]);

  const refresh = useHandler((clearAll = false) => {
    if (clearAll) {
      cache.current.clear();
    } else {
      cache.current.delete(cacheKey);
    }
    setReloadSymbol(Symbol());
  });

  const mutations = useMemo(() => {
    const mutations = {} as { [key in Mutations[number]]: C[key] };
    if (!params.mutations?.length) {
      return mutations;
    }
    for (const mutation of params.mutations) {
      if ((client[mutation] as any).method === "unary") {
        mutations[mutation] = (async (input, options) => {
          try {
            return await client[mutation]!(input, options);
          } finally {
            refresh(true);
          }
        }) as C[typeof mutation];
      } else {
        mutations[mutation] = ((input, options) => {
          try {
            return client[mutation]!(input, options);
          } finally {
            refresh(true);
          }
        }) as C[typeof mutation];
      }
    }

    return mutations;
  }, [params.mutations, client, refresh]);

  return {
    ...(params.condition === false
      ? { pending: false, value: undefined }
      : cachedValue
        ? { pending: false, value: cachedValue }
        : dataLoad),
    refresh,
    mutations,
  };
}
